import { FileText, Shield, AlertCircle } from "lucide-react";

export const Methodology = () => {
  return (
    <div style={{
      maxWidth: "780px",
      margin: "0 auto",
      padding: "4rem 2rem"
    }}>
      {/* Page Title */}
      <h1 style={{
        fontSize: "2.25rem",
        fontWeight: "800",
        color: "#111827",
        marginBottom: "1rem",
        lineHeight: "1.2"
      }}>
        Methodology
      </h1>

      {/* Callout */}
      <div style={{
        background: "linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)",
        border: "1px solid #bfdbfe",
        borderRadius: "10px",
        padding: "1.5rem",
        marginBottom: "3rem",
        textAlign: "center"
      }}>
        <p style={{
          fontSize: "1.15rem",
          fontWeight: "600",
          color: "#1e40af",
          margin: 0,
          lineHeight: "1.6"
        }}>
          Convert MEC PDFs into structured data and neutral analytical views.
        </p>
      </div>

      {/* Overview */}
      <section style={{ marginBottom: "4rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          Overview
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          SHOWME Campaign Finances converts Missouri Ethics Commission (MEC) campaign finance PDF filings into clean, structured spreadsheets and data-derived analytical views.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          Campaign finance data is public, but it is often locked inside PDFs that are difficult to work with at scale. SHOWME removes that friction by extracting the data as reported and presenting it in formats that support efficient review, analysis, and comparison.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          SHOWME is a data preparation and analysis platform. It does not modify reported figures or apply editorial or political judgment.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* Source of Data */}
      <section style={{ marginBottom: "4rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <FileText size={20} style={{ color: "#6b7280", flexShrink: 0 }} />
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#111827",
            margin: 0
          }}>
            Source of Data
          </h2>
        </div>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          All data processed by SHOWME comes directly from official Missouri Ethics Commission (MEC) PDF reports uploaded by users.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          These reports may include:
        </p>
        <ul style={{
          color: "#4b5563",
          lineHeight: "2",
          marginBottom: "1rem",
          fontSize: "1.05rem",
          paddingLeft: "1.5rem"
        }}>
          <li style={{ marginBottom: "0.5rem" }}>Periodic campaign finance filings</li>
          <li style={{ marginBottom: "0.5rem" }}>24-hour reports</li>
          <li style={{ marginBottom: "0.5rem" }}>Amended filings</li>
          <li>Supplemental schedules and addenda</li>
        </ul>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          SHOWME does not scrape third-party websites and does not generate campaign finance data independently. Uploaded PDFs are treated as the authoritative source.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* How Data Is Extracted */}
      <section style={{ marginBottom: "4rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          How Data Is Extracted
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1.5rem",
          fontSize: "1.05rem"
        }}>
          SHOWME uses a structured extraction process designed specifically for the layouts and conventions used in Missouri Ethics Commission filings.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1.5rem",
          fontSize: "1.05rem"
        }}>
          At a high level:
        </p>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.5rem",
            fontSize: "1.05rem"
          }}>
            PDFs are parsed directly
          </p>
          <p style={{
            color: "#4b5563",
            lineHeight: "1.6",
            fontSize: "1.05rem"
          }}>
            Uploaded files are read and processed to identify report sections, tables, and labeled fields.
          </p>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.5rem",
            fontSize: "1.05rem"
          }}>
            Multiple filing formats are supported
          </p>
          <p style={{
            color: "#4b5563",
            lineHeight: "1.6",
            fontSize: "1.05rem"
          }}>
            Missouri campaign finance filings appear in more than one layout depending on report type and filing year. SHOWME accounts for these variations to extract data consistently.
          </p>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.5rem",
            fontSize: "1.05rem"
          }}>
            Data is captured as reported
          </p>
          <p style={{
            color: "#4b5563",
            lineHeight: "1.6",
            fontSize: "1.05rem"
          }}>
            Names, dates, amounts, contribution types, and purposes are extracted directly from the filings without reinterpretation or reclassification.
          </p>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.5rem",
            fontSize: "1.05rem"
          }}>
            Minimal normalization for usability
          </p>
          <p style={{
            color: "#4b5563",
            lineHeight: "1.6",
            fontSize: "1.05rem"
          }}>
            Dates and monetary values are converted into standardized formats so the data can be reliably used in spreadsheets and analytical tools.
          </p>
        </div>

        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          The objective is not to enhance or reinterpret the data, but to make the original filings usable without manual transcription.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* Data-Derived Analysis */}
      <section style={{ marginBottom: "4rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          Data-Derived Analysis
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          SHOWME provides analytical features built directly on top of the extracted campaign finance data.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          These analyses are rules-based and deterministic, meaning they are produced by applying consistent calculations and groupings to the reported figures. They do not introduce new facts or subjective interpretation.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          Examples of data-derived analysis may include:
        </p>
        <ul style={{
          color: "#4b5563",
          lineHeight: "2",
          marginBottom: "1rem",
          fontSize: "1.05rem",
          paddingLeft: "1.5rem"
        }}>
          <li style={{ marginBottom: "0.5rem" }}>Aggregated totals across multiple reports</li>
          <li style={{ marginBottom: "0.5rem" }}>Time-based summaries of contributions and expenses</li>
          <li style={{ marginBottom: "0.5rem" }}>Donor and payee frequency counts</li>
          <li style={{ marginBottom: "0.5rem" }}>Categorization and grouping of transactions</li>
          <li>Committee-level rollups and comparisons</li>
        </ul>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          All analytical outputs reflect the data contained in the uploaded filings. SHOWME does not infer intent, assess compliance, or apply political or legal judgment. Analytical views are provided to help users explore and understand the data more efficiently.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* What Data Is Included */}
      <section style={{ marginBottom: "4rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          What Data Is Included
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1.5rem",
          fontSize: "1.05rem"
        }}>
          Depending on the uploaded reports, extracted data may include:
        </p>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.75rem",
            fontSize: "1.05rem"
          }}>
            Report Information
          </p>
          <ul style={{
            color: "#4b5563",
            lineHeight: "2",
            fontSize: "1.05rem",
            paddingLeft: "1.5rem",
            margin: 0
          }}>
            <li style={{ marginBottom: "0.5rem" }}>Committee name and identifier</li>
            <li style={{ marginBottom: "0.5rem" }}>Filing period and filing date</li>
            <li style={{ marginBottom: "0.5rem" }}>Treasurer name (when present)</li>
            <li style={{ marginBottom: "0.5rem" }}>Report type and amendment status</li>
            <li style={{ marginBottom: "0.5rem" }}>Beginning and ending cash balances</li>
            <li>Summary totals reported by the committee</li>
          </ul>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.75rem",
            fontSize: "1.05rem"
          }}>
            Contributions and Receipts
          </p>
          <ul style={{
            color: "#4b5563",
            lineHeight: "2",
            fontSize: "1.05rem",
            paddingLeft: "1.5rem",
            margin: 0
          }}>
            <li style={{ marginBottom: "0.5rem" }}>Itemized contributions</li>
            <li style={{ marginBottom: "0.5rem" }}>Non-itemized contribution totals</li>
            <li style={{ marginBottom: "0.5rem" }}>Loans and loan repayments (when reported)</li>
            <li style={{ marginBottom: "0.5rem" }}>Miscellaneous receipts included in the filing</li>
            <li>Monetary and in-kind designations as indicated in the report</li>
          </ul>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <p style={{
            color: "#111827",
            fontWeight: "600",
            marginBottom: "0.75rem",
            fontSize: "1.05rem"
          }}>
            Expenses and Disbursements
          </p>
          <ul style={{
            color: "#4b5563",
            lineHeight: "2",
            fontSize: "1.05rem",
            paddingLeft: "1.5rem",
            margin: 0
          }}>
            <li style={{ marginBottom: "0.5rem" }}>Itemized expenses</li>
            <li style={{ marginBottom: "0.5rem" }}>Category-based expenses</li>
            <li style={{ marginBottom: "0.5rem" }}>Contributions made to other committees</li>
            <li>Expense purpose and payment status when available</li>
          </ul>
        </div>

        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          The structure of the original filing is preserved while presenting the data in CSV or Excel format.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* Handling Amended Reports */}
      <section style={{ marginBottom: "4rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <Shield size={20} style={{ color: "#6b7280", flexShrink: 0 }} />
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#111827",
            margin: 0
          }}>
            Handling Amended Reports
          </h2>
        </div>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          Missouri allows committees to file amended versions of previously submitted reports.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          SHOWME handles amended filings as follows:
        </p>
        <ul style={{
          color: "#4b5563",
          lineHeight: "2",
          marginBottom: "1rem",
          fontSize: "1.05rem",
          paddingLeft: "1.5rem"
        }}>
          <li style={{ marginBottom: "0.5rem" }}>Reports are grouped by committee and reporting period.</li>
          <li style={{ marginBottom: "0.5rem" }}>When multiple reports exist for the same committee and period, the most recently filed report is treated as authoritative.</li>
          <li style={{ marginBottom: "0.5rem" }}>If a newer filing supersedes an older one, previously extracted data from the older report is replaced.</li>
          <li>If an older report is uploaded after a newer one, it is recognized as superseded and does not overwrite current data.</li>
        </ul>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          This approach reflects how amended filings are intended to function and helps prevent outdated or duplicate information.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* Accuracy and Limitations */}
      <section style={{ marginBottom: "4rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <AlertCircle size={20} style={{ color: "#6b7280", flexShrink: 0 }} />
          <h2 style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#111827",
            margin: 0
          }}>
            Accuracy and Limitations
          </h2>
        </div>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          SHOWME is designed to extract and present campaign finance data accurately, but users should be aware of the following:
        </p>
        <ul style={{
          color: "#4b5563",
          lineHeight: "2",
          marginBottom: "1rem",
          fontSize: "1.05rem",
          paddingLeft: "1.5rem"
        }}>
          <li style={{ marginBottom: "0.5rem" }}>Extraction quality depends on the completeness and clarity of the source PDFs.</li>
          <li style={{ marginBottom: "0.5rem" }}>Some filings contain inconsistent formatting or incomplete fields.</li>
          <li>Ambiguous markings in original reports are handled conservatively.</li>
        </ul>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          Analytical views are intended to support exploration and understanding. Users should always consult the original Missouri Ethics Commission filings for official totals, legal verification, or compliance purposes.
        </p>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* What SHOWME Does Not Do */}
      <section style={{ marginBottom: "4rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          What SHOWME Does Not Do
        </h2>
        <ul style={{
          color: "#4b5563",
          lineHeight: "2",
          fontSize: "1.05rem",
          paddingLeft: "1.5rem"
        }}>
          <li style={{ marginBottom: "0.5rem" }}>SHOWME does not alter reported figures.</li>
          <li style={{ marginBottom: "0.5rem" }}>SHOWME does not estimate missing data.</li>
          <li style={{ marginBottom: "0.5rem" }}>SHOWME does not infer intent or evaluate legality.</li>
          <li style={{ marginBottom: "0.5rem" }}>SHOWME does not provide political analysis or opinions.</li>
          <li>SHOWME is not affiliated with or endorsed by the Missouri Ethics Commission.</li>
        </ul>
      </section>

      {/* Divider */}
      <div style={{
        height: "1px",
        background: "#e5e7eb",
        marginBottom: "4rem"
      }} />

      {/* Why This Approach */}
      <section style={{ marginBottom: "3rem" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "1rem"
        }}>
          Why This Approach
        </h2>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          marginBottom: "1rem",
          fontSize: "1.05rem"
        }}>
          Campaign finance transparency only works if people can actually work with the data.
        </p>
        <p style={{
          color: "#4b5563",
          lineHeight: "1.6",
          fontSize: "1.05rem"
        }}>
          SHOWME exists to eliminate hours of manual copying, spreadsheet cleanup, and transcription errors — allowing journalists, researchers, campaigns, and the public to focus on analysis and accountability, not data entry.
        </p>
      </section>
    </div>
  );
};
